// src/components/AttivitaScadenzeWidget.jsx
import React, { useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Plus,
  AlertTriangle,
  Clock3,
} from "lucide-react";

function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseYmdToDate(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(dt.getTime()) ? null : dt;
}

function itMonthLabel(date) {
  return date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
}

function itDayLabel(date) {
  return date.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
}

// ✅ normalizza: Date | "YYYY-MM-DD" | ISO string | altre stringhe parseabili
function normalizeDateKey(v, fallbackKey) {
  if (!v) return fallbackKey;
  if (v instanceof Date && !isNaN(v.getTime())) return ymd(v);
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const dt = new Date(v);
    if (!isNaN(dt.getTime())) return ymd(dt);
  }
  return fallbackKey;
}

// ✅ priority dal DB: 2/3/4 (come tuo screenshot) -> normal/medium/high
function normalizePriority(p) {
  if (p == null) return "normal";

  if (typeof p === "string") {
    if (p === "high" || p === "medium" || p === "normal") return p;
    const n = Number(p);
    if (!Number.isNaN(n)) p = n;
  }

  if (typeof p === "number") {
    // dal tuo DB: 4 urgente, 3 in scadenza, 2 normale
    if (p >= 4) return "high";
    if (p === 3) return "medium";
    return "normal";
  }

  return "normal";
}

function priorityToTone(pr) {
  if (pr === "high") return "red";
  if (pr === "medium") return "orange";
  return "blue"; // task normale -> evidenzia comunque
}

function Pill({ tone = "grey", icon, children }) {
  const cls =
    tone === "red"
      ? "bg-rose-100 text-rose-700"
      : tone === "orange"
        ? "bg-orange-100 text-orange-700"
        : tone === "blue"
          ? "bg-sky-100 text-sky-700"
          : "bg-slate-100 text-slate-700";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {icon ? <span className="opacity-80">{icon}</span> : null}
      {children}
    </span>
  );
}

function TaskCard({ item, onOpen, onComplete, showCompleteButton }) {
  const pr = normalizePriority(item.priority || (item.urgent ? "high" : item.warning ? "medium" : "normal"));
  const tone = pr === "high" ? "red" : pr === "medium" ? "orange" : "grey";

  const shell =
    tone === "red"
      ? "border-rose-300 bg-rose-50"
      : tone === "orange"
        ? "border-orange-300 bg-orange-50"
        : "border-slate-200 bg-slate-50";

  const iconWrap =
    tone === "red"
      ? "bg-rose-200/60 text-rose-600"
      : tone === "orange"
        ? "bg-orange-200/60 text-orange-600"
        : "bg-slate-200 text-slate-500";

  const metaDate =
    item.metaDate ||
    (item.date
      ? new Date(parseYmdToDate(String(item.date)) || new Date(item.date)).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
      })
      : null);

  return (
    <div className={`flex items-start justify-between gap-3 rounded-2xl border p-4 ${shell}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {tone === "red" ? (
            <Pill tone="red" icon={<AlertTriangle size={12} />}>
              Urgente
            </Pill>
          ) : tone === "orange" ? (
            <Pill tone="orange" icon={<Clock3 size={12} />}>
              In scadenza
            </Pill>
          ) : null}

          {metaDate ? (
            <Pill tone={tone === "red" ? "red" : tone === "orange" ? "orange" : "grey"}>
              {metaDate}
            </Pill>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onOpen}
          className="mt-2 block w-full text-left text-sm font-medium text-slate-800 hover:underline truncate"
          title={item.label}
        >
          {item.label} <span className="opacity-60">›</span>
        </button>

        {item.assignee ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
            <CircleUserRound size={14} className="opacity-70" />
            <span className="truncate">{item.assignee}</span>
          </div>
        ) : null}
      </div>

      <div className="shrink-0 flex items-center gap-2">
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${iconWrap}`}>
          <CheckCircle2 size={18} />
        </div>

        {showCompleteButton ? (
          <button
            type="button"
            onClick={onComplete}
            className="hidden sm:inline-flex rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
          >
            Completa
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function AttivitaScadenzeWidget({
  tasks,
  done,
  onOpenTask,
  onCompleteTask,
  onRestoreTask,
  onAddTask, // (dateStr, payload) => void  payload: { label, priority }
  scadenzeByDate = null, // Map("YYYY-MM-DD" -> "red|orange|blue")
}) {
  const [month, setMonth] = useState(new Date());
  const [tab, setTab] = useState("todo"); // todo | done
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newPriority, setNewPriority] = useState("normal"); // normal | medium | high

  const todayKey = useMemo(() => ymd(new Date()), []);
  const selectedKey = useMemo(() => ymd(selectedDate), [selectedDate]);

  // ✅ mappa: date -> tone
  const marksByDate = useMemo(() => {
    if (scadenzeByDate instanceof Map) return scadenzeByDate;

    const map = new Map();
    const bump = (dateKey, tone) => {
      // priorità: red > orange > blue
      const cur = map.get(dateKey);
      if (cur === "red") return;
      if (tone === "red") map.set(dateKey, "red");
      else if (tone === "orange") map.set(dateKey, cur === "red" ? "red" : "orange");
      else if (tone === "blue") map.set(dateKey, cur || "blue");
    };

    (tasks || []).forEach((t) => {
      const d = normalizeDateKey(t.date, todayKey);
      const pr = normalizePriority(t.priority || (t.urgent ? "high" : t.warning ? "medium" : "normal"));
      bump(d, priorityToTone(pr));
    });

    (done || []).forEach((t) => {
      const d = normalizeDateKey(t.date, todayKey);
      bump(d, "blue");
    });

    return map;
  }, [tasks, done, scadenzeByDate, todayKey]);

  const tasksForDay = useMemo(() => {
    const list = (tasks || []).filter((t) => normalizeDateKey(t.date, todayKey) === selectedKey);
    // urgenti prima, poi scadenza, poi normali
    return list.sort((a, b) => {
      const ap = normalizePriority(a.priority || (a.urgent ? "high" : a.warning ? "medium" : "normal"));
      const bp = normalizePriority(b.priority || (b.urgent ? "high" : b.warning ? "medium" : "normal"));
      const w = (p) => (p === "high" ? 2 : p === "medium" ? 1 : 0);
      return w(bp) - w(ap);
    });
  }, [tasks, selectedKey, todayKey]);

  const doneForDay = useMemo(() => {
    const list = (done || []).filter((t) => normalizeDateKey(t.date, todayKey) === selectedKey);
    return list.sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
  }, [done, selectedKey, todayKey]);

  function DayContent(props) {
    const dateStr = ymd(props.date);
    const tone = marksByDate.get(dateStr); // "red" | "orange" | "blue"
    const isSelected = dateStr === selectedKey;

    const borderCls =
      tone === "red"
        ? "border-rose-500"
        : tone === "orange"
          ? "border-orange-500"
          : tone === "blue"
            ? "border-[#013768]"
            : "border-slate-200";

    const dotCls =
      tone === "red"
        ? "bg-rose-500"
        : tone === "orange"
          ? "bg-orange-500"
          : tone === "blue"
            ? "bg-[#013768]"
            : null;

    return (
      <div className="relative flex h-[52px] w-[44px] items-start justify-center overflow-visible">
        <div
          className={[
            "mt-[6px] h-[38px] w-[38px] grid place-items-center rounded-full text-[13px] leading-none transition",
            "border",
            isSelected
              ? "bg-[#013768] text-white border-[#013768]"
              : `bg-white text-slate-900 ${borderCls} hover:bg-slate-50`,
            tone && !isSelected ? "font-semibold" : "",
          ].join(" ")}
        >
          {props.date.getDate()}
        </div>

        {dotCls ? (
          <span
            className={`absolute left-1/2 top-[46px] -translate-x-1/2 h-1.5 w-1.5 rounded-full ${dotCls}`}
          />
        ) : null}
      </div>
    );
  }

  const classNames = {
    months: "w-full",
    month: "w-full",
    caption: "flex items-center justify-between px-2 py-1",
    caption_label: "text-[18px] font-medium text-slate-700 capitalize",
    nav: "flex items-center gap-2",
    nav_button:
      "h-9 w-9 rounded-full border border-slate-200 bg-white hover:bg-slate-50 grid place-items-center",
    table: "w-full border-collapse mt-4",
    head_row: "flex justify-between px-1",
    head_cell: "w-[44px] text-center text-[11px] text-slate-400 font-medium",
    row: "flex justify-between px-1 mt-2",
    cell: "w-[44px] h-[52px] grid place-items-center overflow-visible", // ✅ per dot visibile
    day: "rounded-full",
    day_today: "font-semibold",
    day_outside: "text-slate-300 opacity-60",
  };

  const navIcons = {
    PreviousMonth: () => <ChevronLeft size={18} />,
    NextMonth: () => <ChevronRight size={18} />,
  };

  const rightTitle =
    selectedKey === todayKey
      ? "Oggi"
      : itDayLabel(parseYmdToDate(selectedKey) || selectedDate);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;

    // per il DB: normal->2, medium->3, high->4 (coerente con il tuo screenshot)
    const prNum = newPriority === "high" ? 4 : newPriority === "medium" ? 3 : 2;

    const payload = { label, priority: prNum };
    onAddTask?.(selectedKey, payload);

    setNewLabel("");
    setNewPriority("normal");
    setAdding(false);
    setTab("todo");
  };

  return (
    <section
      className="w-full rounded-2xl border border-[#E2E8F0] bg-white"
      style={{
        boxShadow: "0px 2px 4px rgba(0,0,0,0.10), 0px 20px 25px -5px rgba(0,0,0,0.10)",
      }}
    >
      <div className="px-6 pt-5">
        <h2 className="text-2xl font-semibold text-slate-800">Attività e Scadenze</h2>
      </div>

      {/* Desktop: affiancati. Mobile: in colonna */}
      <div className="mt-4 flex flex-col lg:flex-row lg:items-stretch">
        {/* CALENDARIO */}
        <div className="px-6 pb-6 lg:w-[420px] lg:shrink-0 flex justify-center">
          <div className="w-full max-w-[360px]">
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              month={month}
              onMonthChange={setMonth}
              showOutsideDays
              formatters={{ formatCaption: (date) => itMonthLabel(date) }}
              components={{
                IconLeft: navIcons.PreviousMonth,
                IconRight: navIcons.NextMonth,
              }}
              // ✅ MODIFIERS: qui diciamo quali giorni hanno attività
              modifiers={{
                hasTask: (date) => marksByDate.has(ymd(date)),
                hasRed: (date) => marksByDate.get(ymd(date)) === "red",
                hasOrange: (date) => marksByDate.get(ymd(date)) === "orange",
                hasBlue: (date) => marksByDate.get(ymd(date)) === "blue",
              }}
              // ✅ CLASSI SUI GIORNI (bottone)
              modifiersClassNames={{
                hasTask: "task-day",
                hasRed: "task-red",
                hasOrange: "task-orange",
                hasBlue: "task-blue",
              }}
              classNames={{
                months: "w-full",
                month: "w-full",
                caption: "flex items-center justify-between px-2 py-1",
                caption_label: "text-[18px] font-medium text-slate-700 capitalize",
                nav: "flex items-center gap-2",
                nav_button:
                  "h-9 w-9 rounded-full border border-slate-200 bg-white hover:bg-slate-50 grid place-items-center",
                table: "w-full border-collapse mt-3",
                head_row: "flex justify-between px-1",
                head_cell: "w-[44px] text-center text-[11px] text-slate-400 font-medium",
                row: "flex justify-between px-1 mt-1",
                cell: "w-[44px] h-[52px] grid place-items-center overflow-visible",
                day: "day-btn",
                day_button: "day-btn",
                day_selected: "day-selected",
                day_outside: "text-slate-300 opacity-60",
              }}
            /></div>
        </div>

        {/* DIVIDER */}
        <div className="hidden lg:block w-px bg-slate-200/60 my-6" />

        {/* LISTA */}
        <div className="px-6 pb-6 lg:flex-1 lg:min-w-0">
          <div className="flex items-center justify-between gap-3 pt-3 flex-wrap">
            <div className="text-xl font-medium text-slate-800">{rightTitle}</div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setAdding((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Plus size={16} />
                Aggiungi
              </button>

              <div className="rounded-full bg-slate-100 p-1 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setTab("todo")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${tab === "todo"
                    ? "bg-[#013768] text-white shadow"
                    : "text-slate-500 hover:text-slate-700"
                    }`}
                >
                  Attività da fare
                </button>
                <button
                  type="button"
                  onClick={() => setTab("done")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${tab === "done"
                    ? "bg-[#013768] text-white shadow"
                    : "text-slate-500 hover:text-slate-700"
                    }`}
                >
                  Attività Completate
                </button>
              </div>
            </div>
          </div>

          {adding && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-800">Nuova attività</div>

              <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_220px_auto] gap-2">
                <input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Es. Compila checklist igiene personale"
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-300"
                />

                <select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-300"
                >
                  <option value="normal">Normale</option>
                  <option value="medium">In scadenza</option>
                  <option value="high">Urgente</option>
                </select>

                <button
                  type="button"
                  onClick={handleAdd}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Salva
                </button>
              </div>

              <div className="mt-2 text-xs text-slate-500">
                Giorno selezionato: <b>{selectedKey}</b>
              </div>
            </div>
          )}

          <div className="mt-4 max-h-[340px] overflow-auto pr-2">
            {tab === "todo" ? (
              <div className="space-y-3">
                {tasksForDay.length ? (
                  tasksForDay.map((t) => (
                    <TaskCard
                      key={t.id}
                      item={t}
                      onOpen={() => onOpenTask?.(t)}
                      onComplete={() => onCompleteTask?.(t)}
                      showCompleteButton
                    />
                  ))
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    Nessuna attività per questo giorno.
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {doneForDay.length ? (
                  doneForDay.map((t) => (
                    <div key={t.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-700 line-through truncate">
                            {t.label}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            completata alle{" "}
                            {new Date(t.completedAt).toLocaleTimeString("it-IT", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => onRestoreTask?.(t)}
                          className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Ripristina
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    Nessuna attività completata per questo giorno.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}